import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import './table-sort-icon.scss';
import '../../../../src/AssetStatusForm.scss'

type SetPageFunction = React.Dispatch<React.SetStateAction<number>>;

interface TableProps {
    data: any[];
    columns: any[];
    defaultSortColumn: string;
    defaultSortOrder: string;
    columnHeaders: any[];
    pagniation?: boolean;
    page: number;
    setPage: SetPageFunction;
    searchValue: string;
    addedRows: any[];
    handleAddRow: () => void;
    setAddedRows: React.Dispatch<React.SetStateAction<any[]>>;
    setFilterData: React.Dispatch<React.SetStateAction<any[]>>;
    isToggle?: boolean;
    showFTPDataMessage: boolean;
    onHandleRefresh: () => Promise<void>;
    assetId: string;

}

export const FTPTable: React.FC<TableProps> = ({ data, assetId, columns, defaultSortColumn, defaultSortOrder, columnHeaders, pagniation, page, setPage, onHandleRefresh, showFTPDataMessage }) => {

    const [sortColumn, setSortColumn] = useState(defaultSortColumn);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);
    const [showDropdown, setShowDropdown] = useState(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageSelected, setPageSelected] = useState<string>("10")
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState(null);

    const nonSortableColumns = new Set(['actions']);
    const baseUrl = process.env.REACT_APP_SERVER_ENDPOINT;



    useEffect(() => {
        setCurrentPage(page)
    }, [page])

    const handlePageNumberChange = (pageNumber: number) => {
        setPage(pageNumber)
        setCurrentPage(pageNumber);
    }

    const handlePageSelectedClick = (event: any) => {
        const selectedValue: string = event.target.value;
        setPageSelected(selectedValue)
        const newSize = selectedValue?.toLowerCase() === "all" ? data?.length + 1 : Number(selectedValue);

        // Calculate new page number to ensure correct positioning after page size change
        const newPage = Math.floor((currentPage - 1) * pageSize / newSize) + 1;

        // Update the page size
        setPageSize(newSize);

        setPage(newPage);

        // Update the current page to the recalculated value
        setCurrentPage(newPage);
    };

    const handleDelete = (item) => {
        setSelectedItem(item);
        setShowDeleteModal(true);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setSelectedItem(null);
    };

    const handleConfirmDelete = async () => {
        if (!selectedItem) return;
        const headers = {
            'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
            'Content-Type': 'application/json' // Adjust content type if needed
        }

        try {
            await fetch(
                `${baseUrl}/Coredump/delete`,
                {
                    method: "DELETE",
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        fileName: selectedItem,
                        assetId: assetId
                    })
                }
            );

            // Optional: refresh table or remove row from UI
            console.log("File deleted successfully");

        } catch (error) {
            console.error("Delete failed", error);
        } finally {
            setShowDeleteModal(false);
            setSelectedItem(null);
            onHandleRefresh();
        }
    };


    const handleDownload = async (fileName) => {
        try {

            const headers = {
                'Authorization': `Bearer ${localStorage.getItem("jwtAccessToken")}`,
                'Content-Type': 'application/json' // Adjust content type if needed
            }

            const url = `${baseUrl}/Coredump/download?fileName=${encodeURIComponent(fileName)}&assetId=${assetId}`;

            const response = await fetch(url, {
                method: "GET",
                headers: headers
            });

            if (!response.ok) {
                throw new Error("Failed to download file");
            }

            const blob = await response.blob();

            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = fileName; // forces download with filename
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };



    const sortedData = data?.sort((a, b) => {
        const sortKey = sortColumn;
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        const isDateColumn = sortKey === 'date'; // Replace 'date' with your actual date column key
        const isANumber = !isNaN(parseFloat(aValue)) && isFinite(aValue);
        const isBNumber = !isNaN(parseFloat(bValue)) && isFinite(bValue);

        // Handle empty or null strings to move them to top or bottom based on sortOrder
        const isAEmpty = aValue === null || aValue === '';
        const isBEmpty = bValue === null || bValue === '';

        if (isAEmpty && !isBEmpty) return sortOrder === 'asc' ? -1 : 1;
        if (isBEmpty && !isAEmpty) return sortOrder === 'asc' ? 1 : -1;

        if (isDateColumn) {
            // Parse and compare dates
            const aDate = Date.parse(aValue); // Parse the date string
            const bDate = Date.parse(bValue);

            return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
        } else if (isANumber && isBNumber) {
            // Numeric sorting
            return sortOrder === 'asc'
                ? parseFloat(aValue) - parseFloat(bValue)
                : parseFloat(bValue) - parseFloat(aValue);
        } else {
            // String sorting
            return sortOrder === 'asc'
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        }
    });

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedData?.slice(startIndex, endIndex);

    const handleColumnClick = (column: string) => {
        // Check if the column is non-sortable
        if (nonSortableColumns.has(column)) {
            // Do nothing if the column is not sortable
            return;
        }

        // Toggle sorting order if the column is already sorted
        if (column === sortColumn) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set the new column and default to ascending order
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const handlePageClick = (page: number) => {
        handlePageNumberChange && handlePageNumberChange(page);
        setShowDropdown(false)
    };

    const handlePreviousPage = () => {
        setShowDropdown(false)
        if (currentPage > 1)
            handlePageNumberChange && handlePageNumberChange(currentPage - 1);
    };

    const handleNextPage = () => {
        setShowDropdown(false)
        const totalPages = Math.ceil(data?.length / pageSize);
        if (currentPage < totalPages)
            handlePageNumberChange && handlePageNumberChange(currentPage + 1);
    };

    const renderPagination = () => {
        const totalPages = Math.ceil(data?.length / pageSize);

        const generatePaginationNumbers = () => {
            const pages = [];
            const maxVisiblePages = 6; // Maximum visible pages excluding ellipses
            const formatPage = (page) => (page > 999 ? `${page.toString()}` : page);

            if (totalPages <= maxVisiblePages) {
                // If there are 6 or fewer pages, show all pages
                for (let i = 1; i <= totalPages; i++) {
                    pages.push({ value: i, label: formatPage(i) });
                }
            } else {
                if (currentPage <= 3) {
                    pages.push(
                        { value: 1, label: formatPage(1) },
                        { value: 2, label: formatPage(2) },
                        { value: 3, label: formatPage(3) },
                        { value: '...', label: '...' },
                        { value: totalPages - 2, label: formatPage(totalPages - 2) },
                        { value: totalPages - 1, label: formatPage(totalPages - 1) },
                        { value: totalPages, label: formatPage(totalPages) }
                    );
                } else if (currentPage > 3 && currentPage < totalPages - 4) {
                    pages.push(
                        { value: currentPage - 1, label: formatPage(currentPage - 1) },
                        { value: currentPage, label: formatPage(currentPage) },
                        { value: currentPage + 1, label: formatPage(currentPage + 1) },
                        { value: '...', label: '...' },
                        { value: totalPages - 2, label: formatPage(totalPages - 2) },
                        { value: totalPages - 1, label: formatPage(totalPages - 1) },
                        { value: totalPages, label: formatPage(totalPages) }
                    );
                } else {
                    pages.push(
                        { value: totalPages - 5, label: formatPage(totalPages - 5) },
                        { value: totalPages - 4, label: formatPage(totalPages - 4) },
                        { value: totalPages - 3, label: formatPage(totalPages - 3) },
                        { value: totalPages - 2, label: formatPage(totalPages - 2) },
                        { value: totalPages - 1, label: formatPage(totalPages - 1) },
                        { value: totalPages, label: formatPage(totalPages) }
                    );
                }
            }

            return pages;
        };

        const paginationNumbers = generatePaginationNumbers();

        return (
            <div className="flex flex-col md:flex-row items-center justify-between mt-4">
                {/* Left aligned section: Previous button */}
                <button
                    className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] ${currentPage === 1
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                    Previous
                </button>

                {/* Center aligned Pagination */}
                <div className="pagination">
                    {paginationNumbers.map((page, index) => (
                        <button
                            key={index}
                            className={`pagination-button ${currentPage === page.value ? "active" : ""}`}
                            onClick={() => handlePageClick(page.value)}
                        >
                            {page.label}
                        </button>
                    ))}
                </div>


                {/* Right aligned Next button */}
                <button
                    className={`flex items-center text-neutral-25 justify-center rounded-[8px] border-[1px] border-neutral-25 py-[8px] px-[14px] mt-2 md:mt-0 ${currentPage === totalPages
                        ? "bg-darkcolor-300 cursor-not-allowed"
                        : "bg-transparent text-white"
                        }`}
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                >
                    Next
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </button>
            </div>
        );
    };


    const renderSortIcon = (column: string) => {
        if (nonSortableColumns.has(column)) return null;
        const isActive = sortColumn === column;
        return (
            <FontAwesomeIcon
                icon={faSort}
                className={`table-sort-icon ml-1 ${isActive && sortOrder === "asc" ? "table-sort-icon--asc" : ""}`}
            />
        );
    };

    const getColumnColorOrHex = (column: string, item: { [key: string]: any }): string => {
        if (column === "alarms") {
            return item[column] === "OK" ? "green" : "red";
        } else if (column === "lastCom") {
            return item["hexcode"] || ""; // Return hexcode if it exists
        }
        return ""; // Default case
    };


    return (
        sortedData.length > 0 ?
            <div className="mx-auto w-full">
                <div className="bg-darkcolor-900">
                    <div className="mt-8 flow-root">
                        <div className="overflow-x-auto border rounded-lg max-h-[580px]" style={{ borderColor: "rgb(74 84 99 / var(--tw-border-opacity))" }}>
                            <div className="inline-block min-w-full rounded-lg py-1 align-middle bg-darkcolor-700">
                                <table className="min-w-full table-fixed divide-y divide-darkcolor-400 pl-4">
                                    <thead className="min-w-full divide-y divide-darkcolor-400">
                                        <tr>
                                            {columns.filter((column) => column !== "hexcode").map((column, index) => (
                                                <th
                                                    scope="col"
                                                    className={column === "actions"
                                                            ? "px-3 py-3 text-center text-md font-semibold text-white"
                                                            : index === 0
                                                                ? "py-3 pl-5 px-3 text-left text-md font-semibold text-white"
                                                                : "px-3 py-3 text-left text-md font-semibold text-white"}
                                                    key={column}
                                                    onClick={() => handleColumnClick(column)}
                                                    style={{paddingLeft: column === "actions" ? "0.5rem" : "",
                                                        minWidth: column === "actions" ? "170px" : "auto",
                                                        maxWidth: column === "actions" ? "190px" : "150px",
                                                        width: column === "actions" ? "180px" : "auto"
                                                    }}
                                                >
                                                    <div className={`truncate ${column === "actions" ? "max-w-[180px]" : "max-w-[150px]"}`}>
                                                        {columnHeaders[index]}
                                                        {renderSortIcon(column)}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-darkcolor-400">

                                        {/* Render existing data rows */}
                                        {paginatedData.filter((item) => !item.added).map((item, index) => (
                                            <tr key={index}>
                                                {columns
                                                    .map((column) => (
                                                        <td
                                                            key={column}
                                                            style={{
                                                                color: getColumnColorOrHex(column, item),
                                                                wordBreak: "keep-all",
                                                            width: column === "actions" ? "200px" : "auto",
                                                            minWidth: column === "actions" ? "200px" : "auto",
                                                            maxWidth: column === "actions" ? "220px" : "none",
                                                            textAlign: column === "actions" ? "center" : "left"
                                                            }}
                                                            className="px-4 py-4 text-sm text-gray-300"
                                                        >
                                                            {column === "actions" ? (
                                                                <div className="flex flex-row items-center justify-center gap-2 flex-nowrap">
                                                                    {/* DOWNLOAD BUTTON */}
                                                                    <button
                                                                        className="bg-blue-600 text-white px-2 py-2 rounded hover:bg-blue-700 text-xs sm:text-sm"
                                                                        style={{ color: "#0da6f2" }}
                                                                        onClick={() => handleDownload(item?.fileName)}
                                                                    >
                                                                        Download
                                                                    </button>

                                                                    {/* DELETE BUTTON */}
                                                                    <button
                                                                        className="bg-red-600 text-white px-2 py-2 rounded hover:bg-red-700 text-xs sm:text-sm"
                                                                        style={{ color: "#0da6f2" }}
                                                                        onClick={() => handleDelete(item?.fileName)}
                                                                    >
                                                                        Delete
                                                                    </button>


                                                                </div>

                                                            ) : (
                                                                item[column]
                                                            )}


                                                        </td>
                                                    ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div id="custom-tooltip" className="custom-tooltip" />
                </div>
                {pagniation && renderPagination()}

                {showDeleteModal && (

                    <div
                        className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 p-4"
                        onClick={() => handleCancelDelete()} // Close on background click
                    >
                        <div
                            className="bg-darkcolor-600 p-8 rounded-lg shadow-lg w-full sm:max-w-sm md:max-w-md lg:max-w-md"
                            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
                            style={{ width: "80%" }}
                        >
                            <h2 className="text-lg font-semibold text-white text-center">
                                <p className="text-gray-300 mb-6">
                                    Are you sure you want to delete this file?
                                    <br />
                                    <span className="text-red-400">{selectedItem}</span>
                                </p>
                            </h2>

                            <div className="flex justify-center gap-4 mt-6">
                                <button
                                    onClick={async () => {
                                        await handleConfirmDelete();

                                    }}
                                    style={{ float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                                    className="px-6 py-3 bg-primary-700 text-white rounded-md hover:bg-primary-800 transition-all"
                                >
                                    Yes
                                </button>

                                <button
                                    onClick={() => handleCancelDelete()}
                                    style={{ float: "right", backgroundColor: "var(--Primary-Indigo-700, #005F7C)" }}
                                    className="px-6 py-3 bg-gray-400 text-gray-900 rounded-md hover:bg-gray-500 transition-all"
                                >
                                    No
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            : <div className="mx-auto w-full">
                <div className="bg-darkcolor-900">
                    <div className="mt-8 flow-root">
                        <div className="overflow-x-auto border rounded-lg max-h-[580px]" style={{ borderColor: "rgb(74 84 99 / var(--tw-border-opacity))" }}>
                            <div className="inline-block min-w-full rounded-lg py-50 align-middle bg-darkcolor-700"><span style={{ marginLeft: "45%" }}>{showFTPDataMessage ? " No File Found" : "Loading..."}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
};
